/**
 * FactionSetup - Handles faction initialization and starting positions
 */

import { World } from '@/core/ecs/World';
import { createBuildingEntity } from '@/game/entities/createBuilding';
import { tileOccupancy } from '@/game/systems/TileOccupancy';
import { tileHeight, isLand, getSurfaceY } from '@/rendering/terrain/TerrainGenerator';
import { FACTION_POSITIONS, ALL_FACTIONS } from './GameConstants';
import type { FactionId, BuildingType } from '@/types';
import type { FactionManager } from '@/game/factions/FactionManager';
import type { TechSystem } from '@/game/tech/TechSystem';
import type { VisionSystem } from '@/game/systems/VisionSystem';
import type { AIController } from '@/game/ai/AIController';
import type { Camera } from '@/rendering/Camera';
import type { Minimap } from '@/ui/components/Minimap';
import type { GameConfig } from '@/types';

export class FactionSetup {
  private world: World;
  private factionManager: FactionManager;
  private techSystem: TechSystem;
  private visionSystem: VisionSystem;
  private aiController: AIController;
  private camera: Camera;
  private minimap: Minimap;
  private config: GameConfig;

  private defeatedFactions: Set<FactionId> = new Set();
  private activeFactionIds: FactionId[] = [];

  constructor(
    world: World,
    factionManager: FactionManager,
    techSystem: TechSystem,
    visionSystem: VisionSystem,
    aiController: AIController,
    camera: Camera,
    minimap: Minimap,
    config: GameConfig
  ) {
    this.world = world;
    this.factionManager = factionManager;
    this.techSystem = techSystem;
    this.visionSystem = visionSystem;
    this.aiController = aiController;
    this.camera = camera;
    this.minimap = minimap;
    this.config = config;
  }

  /**
   * Find a valid land position near the target coordinates
   */
  findLandPosition(targetX: number, targetZ: number): { x: number; z: number } {
    const h = tileHeight(targetX, targetZ);
    if (isLand(h)) {
      return { x: targetX, z: targetZ };
    }

    for (let radius = 5; radius <= 50; radius += 5) {
      for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 8) {
        const x = targetX + Math.cos(angle) * radius;
        const z = targetZ + Math.sin(angle) * radius;
        const testH = tileHeight(x, z);
        if (isLand(testH)) {
          return { x, z };
        }
      }
    }

    return { x: 0, z: 0 };
  }

  /**
   * Find a valid position for a building that doesn't overlap with existing buildings
   */
  findValidBuildingPosition(targetX: number, targetZ: number, buildingType: BuildingType): { x: number; z: number } {
    const result = tileOccupancy.canPlaceBuilding(targetX, targetZ, buildingType);
    if (result.valid) {
      return { x: targetX, z: targetZ };
    }

    for (let radius = 3; radius <= 30; radius += 3) {
      for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 8) {
        const x = targetX + Math.cos(angle) * radius;
        const z = targetZ + Math.sin(angle) * radius;
        const checkResult = tileOccupancy.canPlaceBuilding(x, z, buildingType);
        if (checkResult.valid) {
          return { x, z };
        }
      }
    }

    return { x: targetX, z: targetZ };
  }

  setupFactions(playerFaction: FactionId): void {
    this.defeatedFactions.clear();
    this.activeFactionIds = [playerFaction];

    tileOccupancy.clear();

    // Initialize tech state for player
    this.techSystem.initFaction(playerFaction);

    // Set minimap home position to player's faction starting area
    const playerStartPos = FACTION_POSITIONS[playerFaction];
    this.minimap.setHomePosition(playerStartPos.x, playerStartPos.z);

    // Find valid land position for player capital
    const playerPos = this.findLandPosition(playerStartPos.x, playerStartPos.z);
    const playerSurfaceY = getSurfaceY(playerPos.x, playerPos.z);
    const capitalEntity = createBuildingEntity(this.world, 'capital', playerFaction, playerPos.x, playerSurfaceY, playerPos.z);
    tileOccupancy.occupyTiles(playerPos.x, playerPos.z, 'capital', capitalEntity);

    // Create starting barracks for player
    const barracksPos = this.findValidBuildingPosition(playerPos.x + 8, playerPos.z, 'barracks');
    const barracksSurfaceY = getSurfaceY(barracksPos.x, barracksPos.z);
    const barracksEntity = createBuildingEntity(this.world, 'barracks', playerFaction, barracksPos.x, barracksSurfaceY, barracksPos.z);
    tileOccupancy.occupyTiles(barracksPos.x, barracksPos.z, 'barracks', barracksEntity);

    // Set player starting gold
    this.factionManager.addGold(playerFaction, this.config.startingGold - 500);

    // Create AI factions
    const aiFactions = ALL_FACTIONS.filter(f => f !== playerFaction);

    for (const factionId of aiFactions) {
      this.activeFactionIds.push(factionId);

      const targetPos = FACTION_POSITIONS[factionId];
      const pos = this.findLandPosition(targetPos.x, targetPos.z);
      const surfaceY = getSurfaceY(pos.x, pos.z);

      const aiCapitalEntity = createBuildingEntity(this.world, 'capital', factionId, pos.x, surfaceY, pos.z);
      tileOccupancy.occupyTiles(pos.x, pos.z, 'capital', aiCapitalEntity);

      const aiBarracksPos = this.findValidBuildingPosition(pos.x + 8, pos.z, 'barracks');
      const aiBarracksSurfaceY = getSurfaceY(aiBarracksPos.x, aiBarracksPos.z);
      const aiBarracksEntity = createBuildingEntity(this.world, 'barracks', factionId, aiBarracksPos.x, aiBarracksSurfaceY, aiBarracksPos.z);
      tileOccupancy.occupyTiles(aiBarracksPos.x, aiBarracksPos.z, 'barracks', aiBarracksEntity);

      this.techSystem.initFaction(factionId);
      this.visionSystem.initFaction(factionId);
      this.aiController.registerAI(factionId);
      this.factionManager.setRelation(playerFaction, factionId, -50);
    }

    // Set camera to player start
    this.camera.setPosition(playerPos.x, playerPos.z);
  }

  getDefeatedFactions(): Set<FactionId> {
    return this.defeatedFactions;
  }

  getActiveFactionIds(): FactionId[] {
    return this.activeFactionIds;
  }

  markFactionDefeated(factionId: FactionId): void {
    this.defeatedFactions.add(factionId);
  }

  isFactionDefeated(factionId: FactionId): boolean {
    return this.defeatedFactions.has(factionId);
  }

  clear(): void {
    this.defeatedFactions.clear();
    this.activeFactionIds = [];
  }
}
